import { useState, useEffect, useRef } from 'react';
import { useBirthInfo } from '../../../contexts/BirthInfoContext';
import { calculateZodiacSign, getZodiacEnglishName, getZodiacTraits } from '../../../utils/astrology';
import {
  calculateFourPillars,
  calculateElementBalance,
  analyzeElements,
  calculateTenGods,
  FourPillars,
  ElementBalance,
  TenGodAnalysis,
} from '../../../utils/fourPillars';
import { usePdfDownload } from '../../../hooks/usePdfDownload';
import FourPillarsChart from '../../../components/charts/FourPillarsChart';
import ElementRadarChart from '../../../components/charts/ElementRadarChart';
import ZodiacWheel from '../../../components/charts/ZodiacWheel';
import FortuneTimeline from '../../../components/charts/FortuneTimeline';
import MonthlyFortuneCalendar from './MonthlyFortuneCalendar';

export default function ReportDisplay() {
  const { birthInfo } = useBirthInfo();
  const { downloadPdf } = usePdfDownload();
  const [activeTab, setActiveTab] = useState<'astrology' | 'fourPillars'>('astrology');
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [zodiacSign, setZodiacSign] = useState<string>('');
  const [zodiacTraits, setZodiacTraits] = useState<{ element: string; quality: string; ruler: string } | null>(null);
  const [fourPillars, setFourPillars] = useState<FourPillars | null>(null);
  const [elementBalance, setElementBalance] = useState<ElementBalance | null>(null);
  const [elementAnalysis, setElementAnalysis] = useState<{ strongest: string; weakest: string; description: string } | null>(null);
  const [tenGods, setTenGods] = useState<TenGodAnalysis | null>(null);
  const reportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (birthInfo) {
      const sign = calculateZodiacSign(birthInfo.birthDate);
      setZodiacSign(sign);
      setZodiacTraits(getZodiacTraits(sign));
      const pillars = calculateFourPillars(birthInfo.birthDate, birthInfo.birthTime);
      setFourPillars(pillars);
      const balance = calculateElementBalance(pillars);
      setElementBalance(balance);
      setElementAnalysis(analyzeElements(balance));
      setTenGods(calculateTenGods(pillars));
    }
  }, [birthInfo]);

  const handleDownloadPdf = () => {
    if (!birthInfo || !fourPillars || !elementBalance || !tenGods) return;
    setIsPdfLoading(true);
    setTimeout(() => {
      downloadPdf({ birthInfo, fourPillars, elementBalance, tenGods, zodiacSign });
      setIsPdfLoading(false);
    }, 300);
  };

  if (!birthInfo) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center text-white">
          <div className="w-16 h-16 border-4 border-gold-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg sm:text-xl">생년월일 정보가 없습니다.</p>
          <p className="text-indigo-300 mt-2 text-sm sm:text-base">홈페이지에서 정보를 입력해주세요.</p>
        </div>
      </div>
    );
  }

  const currentYear = new Date().getFullYear();
  const birthDateObj = new Date(birthInfo.birthDate);
  const birthYear = birthDateObj.getFullYear();
  const birthMonth = birthDateObj.getMonth() + 1;
  const birthDay = birthDateObj.getDate();
  const age = currentYear - birthYear;
  const genderLabel = birthInfo.gender === 'male' ? '남성' : '여성';

  return (
    <div className="w-full" ref={reportRef}>
      {/* ─── 프로필 헤더 카드 ─── */}
      <div className="mb-8 sm:mb-10 relative backdrop-blur-xl bg-gradient-to-br from-[#1a0f2e]/80 to-[#0f0a1e]/80 rounded-3xl border-2 border-primary-gold/40 overflow-hidden shadow-2xl shadow-primary-gold/10">
        <div className="absolute top-0 right-0 w-60 h-60 bg-primary-gold/10 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-40 h-40 bg-primary-purple/20 rounded-full blur-2xl pointer-events-none"></div>

        <div className="relative z-10 p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5 sm:gap-7">
            <div className="relative flex-shrink-0">
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-gradient-to-br from-primary-gold via-amber-500 to-amber-600 flex items-center justify-center shadow-lg shadow-primary-gold/40">
                <span className="text-primary-dark text-3xl sm:text-4xl font-black select-none leading-none" style={{ fontFamily: '"Noto Serif KR", serif' }}>
                  {birthInfo.name ? birthInfo.name.charAt(0) : '命'}
                </span>
              </div>
              <div className="absolute -bottom-2 -right-2 px-2 py-0.5 bg-primary-gold rounded-full">
                <span className="text-primary-dark text-[10px] font-bold whitespace-nowrap">{genderLabel}</span>
              </div>
            </div>

            <div className="flex-1 text-center sm:text-left">
              <h2 className="text-2xl sm:text-3xl font-black text-white mb-1">
                {birthInfo.name ? birthInfo.name : '운명의 주인공'}
                <span className="ml-3 text-primary-gold/70 text-sm font-medium">{age}세</span>
              </h2>
              <p className="text-purple-300 text-sm mb-4 tracking-wide">
                <i className="ri-map-pin-2-line mr-1"></i>{birthInfo.birthPlace} 출생
              </p>
              <div className="flex flex-wrap justify-center sm:justify-start gap-2 sm:gap-3">
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 border border-white/20">
                  <i className="ri-calendar-2-line text-primary-gold text-xs"></i>
                  <span className="text-white text-xs font-medium">{birthYear}년 {birthMonth}월 {birthDay}일</span>
                </div>
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 border border-white/20">
                  <i className="ri-time-line text-primary-gold text-xs"></i>
                  <span className="text-white text-xs font-medium">{birthInfo.birthTime}</span>
                </div>
                {zodiacSign && (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-gold/20 border border-primary-gold/40">
                    <i className="ri-sparkling-line text-primary-gold text-xs"></i>
                    <span className="text-primary-gold text-xs font-bold">{zodiacSign}</span>
                  </div>
                )}
                {fourPillars && (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 border border-white/20">
                    <i className="ri-yin-yang-line text-primary-gold text-xs"></i>
                    <span className="text-white text-xs font-medium">일간 {fourPillars.day.stem}(日干)</span>
                  </div>
                )}
              </div>
            </div>

            {/* PDF 다운로드 버튼 */}
            <div className="flex-shrink-0">
              <button
                onClick={handleDownloadPdf}
                disabled={isPdfLoading || !fourPillars}
                className="flex items-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-primary-gold/20 to-amber-500/20 border-2 border-primary-gold/50 text-primary-gold font-bold text-sm hover:bg-primary-gold/30 hover:border-primary-gold transition-all duration-200 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isPdfLoading ? (
                  <><i className="ri-loader-4-line animate-spin"></i><span>생성 중...</span></>
                ) : (
                  <><i className="ri-file-download-line text-base"></i><span>PDF 저장</span></>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="flex justify-center mb-8 sm:mb-10 px-4">
        <div className="relative backdrop-blur-xl bg-gradient-to-r from-indigo-900/40 to-purple-900/40 rounded-full p-1.5 inline-flex border border-gold-400/30 w-full max-w-xs sm:w-auto sm:max-w-none shadow-lg shadow-gold-400/10">
          <div className={`absolute top-1.5 bottom-1.5 rounded-full bg-gradient-to-r from-gold-400 to-gold-500 transition-all duration-300 ease-out shadow-lg shadow-gold-400/50 ${activeTab === 'astrology' ? 'left-1.5 w-[calc(50%-0.375rem)]' : 'left-[calc(50%+0.375rem)] w-[calc(50%-0.375rem)]'}`}></div>
          <button onClick={() => setActiveTab('astrology')} className={`relative z-10 flex-1 sm:flex-none px-6 sm:px-8 py-3 rounded-full transition-all whitespace-nowrap text-sm sm:text-base font-semibold ${activeTab === 'astrology' ? 'text-indigo-950' : 'text-indigo-200 hover:text-white'}`}>
            <span className="flex items-center justify-center gap-2"><i className="ri-sparkling-line"></i>점성술</span>
          </button>
          <button onClick={() => setActiveTab('fourPillars')} className={`relative z-10 flex-1 sm:flex-none px-6 sm:px-8 py-3 rounded-full transition-all whitespace-nowrap text-sm sm:text-base font-semibold ${activeTab === 'fourPillars' ? 'text-indigo-950' : 'text-indigo-200 hover:text-white'}`}>
            <span className="flex items-center justify-center gap-2"><i className="ri-yin-yang-line"></i>사주팔자</span>
          </button>
        </div>
      </div>

      {activeTab === 'astrology' && (
        <div className="space-y-6 sm:space-y-8">
          {/* Zodiac Wheel */}
          <div className="relative backdrop-blur-xl bg-gradient-to-br from-indigo-900/40 to-purple-900/40 rounded-3xl sm:rounded-[2rem] p-6 sm:p-10 border-2 border-gold-400/30 shadow-2xl shadow-gold-400/10 overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gold-400/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl"></div>
            <div className="relative z-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold-400 to-gold-200 mb-6 sm:mb-8 text-center flex items-center justify-center gap-3">
                <i className="ri-compass-3-line text-gold-400"></i>탄생 차트
              </h2>
              <ZodiacWheel sunSign={zodiacSign} />
              <p className="text-center text-indigo-200 mt-6 sm:mt-8 text-sm sm:text-base">
                당신의 태양 별자리는 <strong className="text-transparent bg-clip-text bg-gradient-to-r from-gold-400 to-gold-200 text-xl sm:text-2xl">{zodiacSign}</strong>입니다
              </p>
            </div>
          </div>

          {/* Sun Sign */}
          <div className="relative backdrop-blur-xl bg-gradient-to-br from-indigo-900/40 to-purple-900/40 rounded-3xl sm:rounded-[2rem] p-6 sm:p-10 border-2 border-gold-400/30 shadow-2xl shadow-gold-400/10 overflow-hidden">
            <div className="absolute top-0 right-0 w-48 h-48 bg-yellow-400/10 rounded-full blur-3xl"></div>
            <div className="relative z-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold-400 to-gold-200 mb-6 sm:mb-8">태양 별자리</h2>
              <div className="flex items-center gap-4 sm:gap-6 mb-6 sm:mb-8">
                <div className="w-16 h-16 sm:w-24 sm:h-24 bg-gradient-to-br from-yellow-400 via-orange-400 to-orange-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg shadow-orange-400/50 animate-pulse">
                  <i className="ri-sun-line text-3xl sm:text-5xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-3xl sm:text-4xl font-bold text-white mb-1">{zodiacSign}</h3>
                  <p className="text-indigo-300 text-base sm:text-lg">{getZodiacEnglishName(zodiacSign)}</p>
                </div>
              </div>
              {zodiacTraits && (
                <div className="grid grid-cols-3 gap-3 sm:gap-4">
                  {[{ label: '원소', value: zodiacTraits.element, icon: 'ri-fire-line' }, { label: '성질', value: zodiacTraits.quality, icon: 'ri-contrast-2-line' }, { label: '지배 행성', value: zodiacTraits.ruler, icon: 'ri-planet-line' }].map((item) => (
                    <div key={item.label} className="bg-gradient-to-br from-white/10 to-white/5 rounded-2xl p-4 sm:p-5 border border-white/10 hover:border-gold-400/50 transition-all">
                      <div className="flex items-center gap-2 mb-2"><i className={`${item.icon} text-gold-400`}></i><p className="text-indigo-300 text-xs">{item.label}</p></div>
                      <p className="text-white font-semibold text-sm sm:text-base">{item.value}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Planet Positions */}
          <div className="relative backdrop-blur-xl bg-gradient-to-br from-indigo-900/40 to-purple-900/40 rounded-3xl sm:rounded-[2rem] p-6 sm:p-10 border-2 border-gold-400/30 shadow-2xl shadow-gold-400/10">
            <h2 className="text-2xl sm:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold-400 to-gold-200 mb-6 sm:mb-8">행성 배치</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-5">
              {[{ icon: 'ri-sun-line', color: 'from-yellow-400 to-orange-500', label: '태양', value: zodiacSign }, { icon: 'ri-moon-line', color: 'from-blue-400 to-blue-600', label: '달', value: '게자리' }, { icon: 'ri-heart-line', color: 'from-pink-400 to-pink-600', label: '금성', value: '천칭자리' }, { icon: 'ri-fire-line', color: 'from-red-400 to-red-600', label: '화성', value: '양자리' }].map((planet) => (
                <div key={planet.label} className="bg-gradient-to-br from-white/10 to-white/5 rounded-2xl p-4 sm:p-5 text-center border border-white/10 hover:border-gold-400/50 transition-all hover:scale-105">
                  <div className={`w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br ${planet.color} rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-lg`}>
                    <i className={`${planet.icon} text-2xl sm:text-3xl text-white`}></i>
                  </div>
                  <p className="text-indigo-300 text-xs mb-1">{planet.label}</p>
                  <p className="text-white font-semibold text-sm sm:text-base">{planet.value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* 월별 운세 캘린더 (점성술 탭) */}
          <div className="relative backdrop-blur-xl bg-gradient-to-br from-indigo-900/40 to-purple-900/40 rounded-3xl sm:rounded-[2rem] p-6 sm:p-10 border-2 border-gold-400/30 shadow-2xl shadow-gold-400/10">
            <h2 className="text-2xl sm:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold-400 to-gold-200 mb-2 flex items-center gap-3">
              <i className="ri-calendar-2-line text-gold-400"></i>월별 운세 캘린더
            </h2>
            <p className="text-indigo-300 text-sm mb-7">월을 클릭하면 5가지 분야별 세부 운세를 확인할 수 있습니다</p>
            <MonthlyFortuneCalendar birthDate={birthInfo.birthDate} birthTime={birthInfo.birthTime} gender={birthInfo.gender} />
          </div>

          {/* 10 Year Fortune */}
          <div className="relative backdrop-blur-xl bg-gradient-to-br from-indigo-900/40 to-purple-900/40 rounded-3xl sm:rounded-[2rem] p-6 sm:p-10 border-2 border-gold-400/30 shadow-2xl shadow-gold-400/10">
            <h2 className="text-2xl sm:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold-400 to-gold-200 mb-6 sm:mb-8">10년 운세 흐름</h2>
            <FortuneTimeline startYear={currentYear} years={10} />
          </div>

          {/* Summary */}
          <div className="relative backdrop-blur-xl bg-gradient-to-br from-indigo-900/40 to-purple-900/40 rounded-3xl sm:rounded-[2rem] p-6 sm:p-10 border-2 border-gold-400/30 shadow-2xl shadow-gold-400/10">
            <h2 className="text-2xl sm:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold-400 to-gold-200 mb-6 sm:mb-8">종합 운세</h2>
            <div className="space-y-4 sm:space-y-5 text-indigo-100 leading-relaxed text-sm sm:text-base">
              <p className="bg-gradient-to-r from-white/5 to-transparent p-4 rounded-xl border-l-4 border-gold-400">
                당신의 태양 별자리는 <strong className="text-gold-400">{zodiacSign}</strong>입니다.
                {zodiacTraits?.element === '불' && ' 불의 원소를 가진 당신은 열정적이고 활동적인 에너지를 지니고 있습니다.'}
                {zodiacTraits?.element === '흙' && ' 흙의 원소를 가진 당신은 안정적이고 실용적인 성향을 지니고 있습니다.'}
                {zodiacTraits?.element === '공기' && ' 공기의 원소를 가진 당신은 지적이고 소통을 중시하는 성향을 지니고 있습니다.'}
                {zodiacTraits?.element === '물' && ' 물의 원소를 가진 당신은 감성적이고 직관적인 성향을 지니고 있습니다.'}
              </p>
              <p className="bg-gradient-to-r from-white/5 to-transparent p-4 rounded-xl border-l-4 border-purple-400">{zodiacTraits?.ruler}의 영향을 받아 독특한 개성과 재능을 발휘할 수 있습니다. 현재 행성의 배치는 당신에게 새로운 기회와 도전을 가져다줄 것입니다.</p>
              <p className="bg-gradient-to-r from-white/5 to-transparent p-4 rounded-xl border-l-4 border-blue-400">앞으로 3개월간은 특히 인간관계와 커리어 측면에서 중요한 변화가 예상됩니다. 긍정적인 마음가짐을 유지하며 기회를 포착하세요.</p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'fourPillars' && fourPillars && (
        <div className="space-y-6 sm:space-y-8">
          {/* Four Pillars Chart */}
          <div className="relative backdrop-blur-xl bg-gradient-to-br from-indigo-900/40 to-purple-900/40 rounded-3xl sm:rounded-[2rem] p-6 sm:p-10 border-2 border-gold-400/30 shadow-2xl shadow-gold-400/10 overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gold-400/10 rounded-full blur-3xl"></div>
            <div className="relative z-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold-400 to-gold-200 mb-6 sm:mb-8 flex items-center justify-center gap-3">
                <i className="ri-ancient-gate-line text-gold-400"></i>사주팔자
              </h2>
              <FourPillarsChart pillars={fourPillars} />
            </div>
          </div>

          {/* 십신 분석 */}
          {tenGods && (
            <div className="relative backdrop-blur-xl bg-gradient-to-br from-indigo-900/40 to-purple-900/40 rounded-3xl sm:rounded-[2rem] p-6 sm:p-10 border-2 border-gold-400/30 shadow-2xl shadow-gold-400/10 overflow-hidden">
              <div className="absolute top-0 right-0 w-48 h-48 bg-amber-400/10 rounded-full blur-3xl"></div>
              <div className="relative z-10">
                <h2 className="text-2xl sm:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold-400 to-gold-200 mb-2 flex items-center gap-3">
                  <i className="ri-node-tree text-gold-400"></i>십신(十神) 분석
                </h2>
                <p className="text-indigo-300 text-sm mb-7">일간 <strong className="text-primary-gold">{tenGods.dayStem}(日干)</strong> 기준으로 각 기둥의 십신 관계를 분석합니다</p>
                <div className="overflow-x-auto mb-7">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-3 pr-4 text-indigo-300 font-medium whitespace-nowrap">기둥</th>
                        <th className="text-center py-3 px-3 text-indigo-300 font-medium whitespace-nowrap">천간</th>
                        <th className="text-center py-3 px-3 text-primary-gold font-semibold whitespace-nowrap">십신</th>
                        <th className="text-center py-3 px-3 text-indigo-300 font-medium whitespace-nowrap">지지</th>
                        <th className="text-center py-3 pl-3 text-primary-gold font-semibold whitespace-nowrap">지장간십신</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {tenGods.entries.map((entry) => (
                        <tr key={entry.pillar} className="hover:bg-white/5 transition-colors">
                          <td className="py-3 pr-4 text-white font-medium text-xs sm:text-sm whitespace-nowrap">{entry.pillar}</td>
                          <td className="py-3 px-3 text-center"><span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-white/10 text-white font-bold text-base">{entry.stem}</span></td>
                          <td className="py-3 px-3 text-center">{entry.stemGod === '일원(日元)' ? <span className="px-2 py-1 rounded-full bg-primary-gold/30 text-primary-gold text-xs font-bold whitespace-nowrap">{entry.stemGod}</span> : <span className="px-2 py-1 rounded-full bg-white/10 text-indigo-200 text-xs font-medium whitespace-nowrap">{entry.stemGod}</span>}</td>
                          <td className="py-3 px-3 text-center"><span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-white/10 text-purple-200 font-bold text-base">{entry.branch}</span></td>
                          <td className="py-3 pl-3 text-center"><span className="px-2 py-1 rounded-full bg-white/10 text-indigo-300 text-xs font-medium whitespace-nowrap">{entry.branchGod}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div>
                  <p className="text-indigo-300 text-xs font-semibold tracking-widest uppercase mb-3">주요 십신 특성</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {tenGods.dominantGods.map((item) => (
                      <div key={item.god} className="flex items-start gap-3 p-4 rounded-xl bg-white/5 border border-white/10 hover:border-primary-gold/30 transition-all">
                        <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-primary-gold/30 to-amber-600/20 flex items-center justify-center">
                          <span className="text-primary-gold font-black text-sm">{item.count}</span>
                        </div>
                        <div><p className="text-white font-bold text-sm">{item.god}</p><p className="text-indigo-300 text-xs mt-0.5">{item.shortMeaning}</p></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 월별 운세 캘린더 (사주 탭) */}
          <div className="relative backdrop-blur-xl bg-gradient-to-br from-indigo-900/40 to-purple-900/40 rounded-3xl sm:rounded-[2rem] p-6 sm:p-10 border-2 border-gold-400/30 shadow-2xl shadow-gold-400/10">
            <h2 className="text-2xl sm:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold-400 to-gold-200 mb-2 flex items-center gap-3">
              <i className="ri-calendar-2-line text-gold-400"></i>월별 운세 캘린더
            </h2>
            <p className="text-indigo-300 text-sm mb-7">사주 기운에 따른 월별 흐름을 확인하고 미리 준비하세요</p>
            <MonthlyFortuneCalendar birthDate={birthInfo.birthDate} birthTime={birthInfo.birthTime} gender={birthInfo.gender} />
          </div>

          {/* Element Analysis */}
          {elementBalance && elementAnalysis && (
            <div className="relative backdrop-blur-xl bg-gradient-to-br from-indigo-900/40 to-purple-900/40 rounded-3xl sm:rounded-[2rem] p-6 sm:p-10 border-2 border-gold-400/30 shadow-2xl shadow-gold-400/10 overflow-hidden">
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl"></div>
              <div className="relative z-10">
                <h2 className="text-2xl sm:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold-400 to-gold-200 mb-6 sm:mb-8 text-center flex items-center justify-center gap-3">
                  <i className="ri-donut-chart-line text-gold-400"></i>오행 분석
                </h2>
                <ElementRadarChart balance={elementBalance} />
                <div className="bg-gradient-to-br from-white/10 to-white/5 rounded-2xl p-5 sm:p-7 mt-6 sm:mt-8 border border-gold-400/30">
                  <div className="flex items-start gap-4 sm:gap-5 mb-4 sm:mb-5">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-gold-400 to-gold-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg shadow-gold-400/50">
                      <i className="ri-star-line text-xl sm:text-2xl text-white"></i>
                    </div>
                    <div>
                      <p className="text-white font-bold mb-2 text-base sm:text-lg">가장 강한 오행: <span className="text-gold-400">{elementAnalysis.strongest}</span></p>
                      <p className="text-indigo-200 text-sm sm:text-base">가장 약한 오행: <span className="text-purple-300">{elementAnalysis.weakest}</span></p>
                    </div>
                  </div>
                  <p className="text-indigo-100 leading-relaxed text-sm sm:text-base bg-gradient-to-r from-white/5 to-transparent p-4 rounded-xl border-l-4 border-gold-400">{elementAnalysis.description}</p>
                </div>
              </div>
            </div>
          )}

          {/* 30 Year Fortune */}
          <div className="relative backdrop-blur-xl bg-gradient-to-br from-indigo-900/40 to-purple-900/40 rounded-3xl sm:rounded-[2rem] p-6 sm:p-10 border-2 border-gold-400/30 shadow-2xl shadow-gold-400/10">
            <h2 className="text-2xl sm:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold-400 to-gold-200 mb-6 sm:mb-8">30년 운세 흐름</h2>
            <FortuneTimeline startYear={currentYear} years={30} />
          </div>

          {/* Interpretation */}
          <div className="relative backdrop-blur-xl bg-gradient-to-br from-indigo-900/40 to-purple-900/40 rounded-3xl sm:rounded-[2rem] p-6 sm:p-10 border-2 border-gold-400/30 shadow-2xl shadow-gold-400/10">
            <h2 className="text-2xl sm:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold-400 to-gold-200 mb-6 sm:mb-8">사주 해석</h2>
            <div className="space-y-4 sm:space-y-5 text-indigo-100 leading-relaxed text-sm sm:text-base">
              <p className="bg-gradient-to-r from-white/5 to-transparent p-4 rounded-xl border-l-4 border-gold-400">
                일주가 <strong className="text-gold-400">{fourPillars.day.combined}</strong>인 당신은
                {(fourPillars.day.stem === '갑' || fourPillars.day.stem === '을') && ' 목 기운의 영향으로 성장과 발전의 에너지를 가지고 있습니다.'}
                {(fourPillars.day.stem === '병' || fourPillars.day.stem === '정') && ' 화 기운의 영향으로 열정과 창의성이 뛰어납니다.'}
                {(fourPillars.day.stem === '무' || fourPillars.day.stem === '기') && ' 토 기운의 영향으로 안정적이고 신뢰감 있는 성품을 지녔습니다.'}
                {(fourPillars.day.stem === '경' || fourPillars.day.stem === '신') && ' 금 기운의 영향으로 결단력과 추진력이 강합니다.'}
                {(fourPillars.day.stem === '임' || fourPillars.day.stem === '계') && ' 수 기운의 영향으로 지혜롭고 유연한 사고를 가지고 있습니다.'}
              </p>
              <p className="bg-gradient-to-r from-white/5 to-transparent p-4 rounded-xl border-l-4 border-purple-400">{elementAnalysis?.description}</p>
              <p className="bg-gradient-to-r from-white/5 to-transparent p-4 rounded-xl border-l-4 border-blue-400">년주 <strong className="text-blue-300">{fourPillars.year.combined}</strong>는 조상과 부모로부터 받은 기운을 나타내며, 월주 <strong className="text-green-300">{fourPillars.month.combined}</strong>는 형제 관계와 직업운을 보여줍니다. 시주 <strong className="text-purple-300">{fourPillars.hour.combined}</strong>는 자녀운과 말년운을 의미합니다.</p>
              <p className="bg-gradient-to-r from-white/5 to-transparent p-4 rounded-xl border-l-4 border-gold-400">전체적으로 균형 잡힌 사주를 가지고 있으며, 꾸준한 노력으로 인생의 목표를 달성할 수 있는 좋은 운세입니다.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
